import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../api/api';
import { getLastUpdatedTimeWithUnit } from '../utils/TimeUtil';
import { formatDisplayDate } from '../utils/DateUtil';
import './AuthContent.css';

const AuthContent = ({
    cameFromGoalsEditor = false,
}) => {
    const navigate = useNavigate();
    const ALL_ACTIVITIES_VALUE = 101;
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState(null);
    const [needsStravaReauth, setNeedsStravaReauth] = useState(false);
    const [mostRecentActivityDate, setMostRecentActivityDate] = useState("Unknown");
    const [oldestActivityDate, setOldestActivityDate] = useState(null);
    const [progressesByYears, setProgressesByYears] = useState([]);
    const [yearsVisible, setYearsVisible] = useState([]);
    const [numActivities, setNumActivities] = useState(5);
    const [refreshStreams, setRefreshStreams] = useState(false);
    const currentYearRef = useRef(null);
    const hasScrolledToCurrentYearRef = useRef(false);

    const updateProgressState = (progresses, lastUpdated, firstRun = false) => {
        const oldProgress = JSON.stringify(JSON.parse(localStorage.getItem("progress"))?.progresses);
        localStorage.setItem("progress", JSON.stringify({
            "progresses": progresses,
            "lastUpdated": lastUpdated,
        }));
        const newProgress = JSON.stringify(JSON.parse(localStorage.getItem("progress"))?.progresses);
        setMostRecentActivityDate(progresses.mostRecentActivityDate);
        setOldestActivityDate(progresses.oldestActivityDate);
        if (!firstRun && oldProgress === newProgress) {
            return;
        }

        const progressesMap = progresses?.goalsProgress?.map((progress, _) => {
            return getProgressProcessed(progress);
        });
        const progressesByYears = progressesMap?.reduce((acc, progress) => {
            const endDateRaw = progress["End Date"] ?? "";
            //minus one day to get the correct year
            const endDateEpoch = new Date(endDateRaw)
            endDateEpoch.setDate(endDateEpoch.getDate() - 1);

            const endDate = new Date(endDateEpoch).toISOString();
            const year = endDate.split("-")[0];
            if (!year) {
                return acc;
            }
            if (!acc[year]) {
                acc[year] = [];
            }
            acc[year].push(progress);
            return acc;
        }
        , {});
        setProgressesByYears(progressesByYears);
        if (progressesByYears) {
            const yearsVisible = Object.keys(progressesByYears).map((year) => {
                return {
                    "year": year,
                    // eslint-disable-next-line eqeqeq
                    "visible": year == new Date().getFullYear(),
                }
            });
            setYearsVisible(yearsVisible);
        }
    }

    const getProgressProcessed = (progress) => {
        const numberWithCommas = (num) => {
            const numericValue = Number(num);
            return Number.isFinite(numericValue)
                ? new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(numericValue)
                : String(num);
        }
        const valueWithUnit = (value, unit) => {
            let processedUnit = unit;
            let processedValue = value ?? "0";
            if (processedUnit === "Hours") {
                const isNegative = value < 0;
                const absValue = Math.abs(value);
                if (absValue < 1) {
                    // Less than 1 hour: show as minutes
                    processedValue = Math.round(absValue * 60);
                    processedUnit = "mins";
                    return `${isNegative ? '-' : ''}${numberWithCommas(processedValue)} ${processedUnit}`;
                } else {
                    // 1 hour or more: show as X hours Y minutes if not whole hour
                    const hours = Math.floor(absValue);
                    const minutes = Math.round((absValue - hours) * 60);
                    if (minutes > 0) {
                        return `${isNegative ? '-' : ''}${hours} hr${hours !== 1 ? 's' : ''} ${minutes} min${minutes !== 1 ? 's' : ''}`;
                    } else {
                        return `${isNegative ? '-' : ''}${hours} hr${hours !== 1 ? 's' : ''}`;
                    }
                }
            }
            const displayUnit = processedUnit === "Km" ? "kms" : processedUnit.toLowerCase();
            return numberWithCommas(processedValue) + " " + displayUnit;
        }
        const averageWithWeeklyTotal = (value, unit) => {
            const numericValue = Number(value ?? 0);
            return `${valueWithUnit(value, unit)}/day | ${valueWithUnit(numericValue * 7, unit)}/week`;
        }
        let unit = (progress?.unit) ?? "";
        let totalsText = ""
        if (progress.activityTypes.length > 0) {
            for (let i = 0; i < progress.activityTypes.length; i++) {
                const progressActivityType = progress.activityTypes[i];
                const progressValue = progress.progressByActivityType[i];
                if (progressValue === 0) {
                    continue;
                }
                const activityTypeNameRaw = progressActivityType.toLowerCase();
                const getActivityTypeName = (activityTypeNameRaw) => {
                    switch (activityTypeNameRaw) {
                        case 'weighttraining':
                            return 'Weight Training';
                        default:
                            return activityTypeNameRaw.charAt(0).toUpperCase() + activityTypeNameRaw.slice(1);
                    }
                }
                const activityTypeName = getActivityTypeName(activityTypeNameRaw);
                totalsText += activityTypeName +
                    ": " + 
                    valueWithUnit(progressValue, unit) + 
                    "\n"
            }
        }
        return {
            "Goal": (progress?.goalName ?? ""),
            "Goal Category": (progress?.goalCategoryName ?? ""),
            "Start Date": (progress?.startDate ?? ""),
            "End Date": (progress?.endDate ?? ""),
            "Totals Text": totalsText,
            "Target": valueWithUnit(progress?.goalValue, unit),
            "Target average per day": averageWithWeeklyTotal(progress?.targetAverage, unit),
            "So far": valueWithUnit(progress?.totalProgress, unit) + ", " + (progress?.percentageDone ?? "") + "%",
            "To go": valueWithUnit(progress?.left, unit) + ", " + (progress?.percentageLeft ?? "") + "%",
            "New average per day": averageWithWeeklyTotal(progress?.newAverage, unit),
            "Pace difference": valueWithUnit(progress?.paceDifference, unit),
            "Percentage pace difference": (progress?.percentagePaceDifference ?? "") + "%",
            "percentageTimePassed": (progress?.percentageTimePassed ?? "") + "%",
        }
    }

    const getProgressFromServer = (numActivitiesParam, forceStreamRefresh = refreshStreams) => {
        setIsRefreshing(true);
        setRefreshError(null);
        setNeedsStravaReauth(false);
        const activitiesToFetch = Number(numActivitiesParam) === ALL_ACTIVITIES_VALUE ? "all" : numActivitiesParam;
        api.getProgress(activitiesToFetch, forceStreamRefresh)
            .then(({ data }) => {
                // Only bust the activities cache when new activities actually
                // showed up (e.g. a Strava sync), not on every poll/refresh.
                const activitiesChanged = data.mostRecentActivityDate !== mostRecentActivityDate;
                updateProgressState(data, Date.now());
                if (activitiesChanged) {
                    api.invalidateActivitiesCache();
                }
                setIsRefreshing(false);
            })
            .catch((error) => {
                console.error(error);
                setRefreshError(api.getErrorMessage(error, 'Unable to refresh activities.'));
                setNeedsStravaReauth(Boolean(error.response?.data?.needsReauth));
                setIsRefreshing(false);
            });
    }

    const getProgress = (numActivitiesParam) => {
        let progress;
        try {
            progress = JSON.parse(localStorage.getItem("progress"));
        } catch (e) {
            progress = null;
        }
        const progresses = progress?.progresses ?? "Loading";
        const lastUpdated = progress?.lastUpdated ?? 0;
        updateProgressState(progresses, lastUpdated, true);
        const millisecondsInSecond = 1000;
        const thresholdInMilliseconds = 2 * millisecondsInSecond;
        const now = Date.now()
        if (
            !isRefreshing &&
            (cameFromGoalsEditor || now - lastUpdated >= thresholdInMilliseconds)
        ) {
            getProgressFromServer(numActivitiesParam);
        }
    }

    useEffect(() => {
        getProgress(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const millisecondsInSecond = 1000;
    const secondsInterval = 60;
    useEffect(() => {
        const progressRefreshInterval = setInterval(
            () => getProgress(0),
            secondsInterval * millisecondsInSecond
        );
        return () => clearInterval(progressRefreshInterval);
    // The interval intentionally uses the existing zero-activity refresh mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Jump to the current year's goals once they first render — the
    // minute-by-minute progress refresh re-renders the list too, but
    // shouldn't yank the page back, hence the once-per-mount guard.
    useEffect(() => {
        if (!hasScrolledToCurrentYearRef.current && currentYearRef.current) {
            currentYearRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
            hasScrolledToCurrentYearRef.current = true;
        }
    }, [progressesByYears]);

    const handleSliderChange = (event) => {
        setNumActivities(event.target.value);
    };

    const convertToLocalTime = (utcDate) => {
        if (!utcDate) return "Unknown";
        const date = new Date(utcDate);
        const now = new Date();
        const time = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
        });

        const isToday = date.toDateString() === now.toDateString();
        if (isToday) return `Today, ${time}`;

        const yesterday = new Date();
        yesterday.setDate(now.getDate() - 1);
        const isYesterday = date.toDateString() === yesterday.toDateString();
        if (isYesterday) return `Yesterday, ${time}`;

        const dayOfWeek = date.toLocaleString("en-US", { weekday: "long" });
        const isSameWeek = date > new Date(now.setDate(now.getDate() - now.getDay()));
        if (isSameWeek) return `${dayOfWeek}, ${time}`;

        return `${formatDisplayDate(date)}, ${time}`;
    };

    return (
        <div className="auth-content">
            {refreshError && (
                <div className="refresh-error" role="alert">
                    {refreshError}
                    {needsStravaReauth && (
                        <>
                            {' '}
                            <button type="button" className="refresh-error-action" onClick={() => navigate('/settings')}>
                                Reconnect Strava
                            </button>
                        </>
                    )}
                </div>
            )}
            <div className="activity-controls">
                <button
                    className="refresh-button"
                    id="refresh_button" 
                    onClick={() => getProgressFromServer(numActivities)}
                    disabled={isRefreshing}
                >
                    {isRefreshing ? "Refreshing" : "Refresh"}
                </button>
                <label className="slider-container" htmlFor="activity-count">
                    <span className="slider-label">Recent activities</span>
                    <input
                        id="activity-count"
                        type="range"
                        min="0"
                        max={ALL_ACTIVITIES_VALUE}
                        step="1"
                        value={numActivities}
                        onChange={handleSliderChange}
                        className="slider"
                    />
                    <span className="slider-value">
                        {Number(numActivities) === ALL_ACTIVITIES_VALUE ? "All activities" : `${numActivities} ${Number(numActivities) === 1 ? "activity" : "activities"}`}
                    </span>
                </label>
                <label className="stream-refresh-option" htmlFor="refresh-streams">
                    <input
                        id="refresh-streams"
                        type="checkbox"
                        checked={refreshStreams}
                        onChange={(event) => setRefreshStreams(event.target.checked)}
                    />
                    Refresh streams
                </label>
            </div>
            <div className="activity-status" aria-live="polite">
                <span className="status-dot" aria-hidden="true" />
                <span>Last activity</span>
                <strong id="most_recent_activity_date">
                    {convertToLocalTime(mostRecentActivityDate)}
                </strong>
                {oldestActivityDate && <small>Since {formatDisplayDate(oldestActivityDate)}</small>}
            </div>
            {Object.entries(progressesByYears ?? {}).sort((a, b) => a[0] < b[0] ? 1 : -1).map(([year, progressesByYear]) => (
                <div key={year} className="progress-year" ref={year === String(new Date().getFullYear()) ? currentYearRef : undefined}>
                    <div className="progress-year-inner">
                        <button
                            className="year-toggle"
                            onClick={() => {
                                const newYearsVisible = yearsVisible.map((yearVisible) => ({
                                    "year": yearVisible.year,
                                    "visible": yearVisible.year === year ? !yearVisible.visible : yearVisible.visible,
                            }));
                            setYearsVisible(newYearsVisible);
                        }}
                            aria-expanded={yearsVisible.find((yearVisible) => yearVisible.year === year)?.visible}
                        >
                            <span>{year}</span>
                            <span className="year-toggle-icon" aria-hidden="true">⌄</span>
                        </button>
                        <div style={{ display: yearsVisible.find((yearVisible) => yearVisible.year === year)?.visible ? "block" : "none" }}>
                            {progressesByYear.sort((a, b) => a["Target"] > b["Target"] ? 1 : -1).map((progress, index) => (
                                <div key={index} className="progress-goal">
                                    <div className="goal-header">
                                        <div>
                                            <h2>{progress["Goal"]}</h2>
                                            <div className="goal-category" style={{ display: progress["Goal Category"] === "" ? "none" : "block" }}>
                                                {progress["Goal Category"]}
                                            </div>
                                        </div>
                                        <div className="goal-dates">
                                            <span>{progress["Start Date"] && formatDisplayDate(`${progress["Start Date"]}T00:00:00`)}</span>
                                            <span aria-hidden="true">to</span>
                                            <span>{progress["End Date"] && formatDisplayDate(`${progress["End Date"]}T00:00:00`)}</span>
                                        </div>
                                    </div>
                                    <div className="totals-panel">
                                        <span className="section-label">Activity totals</span>
                                        <div className="totals-text">{progress["Totals Text"]}</div>
                                    </div>
                                    <div className="goal-summary">
                                        <div className="summary-item target">
                                            <span className="section-label">Target</span>
                                            <strong>{progress["Target"]}</strong>
                                        </div>
                                        <div className="summary-item so-far">
                                            <span className="section-label">So far</span>
                                            <strong>{progress["So far"]}</strong>
                                        </div>
                                        <div className="summary-item to-go">
                                            <span className="section-label">To go</span>
                                            <strong>{progress["To go"]}</strong>
                                        </div>
                                    </div>
                                    <div className="progress-track-group">
                                        <div className="progress-track-labels">
                                            <span>Progress</span>
                                            <strong>{progress["So far"].split(", ")[1]}</strong>
                                        </div>
                                        <div className="progress-bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={parseFloat(progress["So far"].split(", ")[1])} aria-label={`${progress["Goal"]} progress`}>
                                            <div
                                                className="progress-bar-green"
                                                style={{ width: Math.min(progress["So far"].split(", ")[1].split("%")[0], "100") + "%" }}
                                            />
                                            <div
                                                className="progress-bar-red"
                                                style={{ width: progress["percentageTimePassed"] }}
                                            />
                                            <div
                                                className="progress-bar-mixed"
                                                style={{ width: Math.min(progress["So far"].split(", ")[1].split("%")[0], progress["percentageTimePassed"].split("%")[0]) + "%" }}
                                            />
                                        </div>
                                    </div>
                                    <div className="goal-details">
                                        <div className="average-group">
                                            <div className="target-avg"><span className="section-label">Target average</span><strong>{progress["Target average per day"]}</strong></div>
                                            <div className="new-avg"><span className="section-label">New average</span><strong>{progress["New average per day"]}</strong></div>
                                        </div>
                                        <div className={`pace-group${progress["Pace difference"].includes("-") ? " negative" : ""}`}>
                                            <span className="section-label">Pace difference</span>
                                            <strong>{progress["Pace difference"]}</strong>
                                            <span className="pace-percent">{progress["Percentage pace difference"]}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default AuthContent;